<template>
  <div
    id="tour-payroll"
    class="payment-table"
  >
    <b-overlay :show="isProcessing">
      <b-row class="justify-content-between align-items-center">
        <b-col
          sm="12"
          md="6"
        >
          <h4 class="mb-2">
            {{ $t('payments.generate-payroll') }} :
            <br class="d-md-none">
            <span
              class="text-muted ml-md-2"
              :style="IS_MOBILE ? { 'font-size': '16px' } : ''"
            >
              {{ FORMAT_DATE_RANGE(form.start_date, form.end_date) }}
            </span>
          </h4>
        </b-col>
        <b-col
          class="d-flex align-items-center justify-content-end"
          sm="12"
          md="6"
        >
          <div
            v-if="canViewUsers"
            class="d-flex align-items-center mr-2"
          >
            <b-form-checkbox
              id="inactive-user-switch"
              v-model="includeInactive"
              class="colorful-switch"
              switch
              @change="getPayments"
            />
            <label for="inactive-user-switch">
              <h6 class="cursor-pointer mb-0">
                {{ $t("Include Inactive Users") }}
              </h6>
            </label>
          </div>
          <div class="mr-2 text-muted text-center">
            <b>{{ $t('payments.total-time') }}</b>
            <h4 class="ml-1">
              {{ CONVERT_HM(totalTime) }}
            </h4>
          </div>
          <div class="text-center text-muted">
            <b>{{ $t('payments.total-amount') }}</b>
            <h4
              class="ml-1"
              style="cursor: pointer;"
              :class="!isAmountBlur ? 'blurry-text' : ''"
              @click="isAmountBlur = !isAmountBlur"
            >
              {{ rows[0] ? rows[0].currency : '-' }} {{ number_format(totalAmount, 2) }}
            </h4>
          </div>

          <manage-permissions
            v-if="canManagePermissions"
            class="ml-2"
            :permissions="permissions"
          />
        </b-col>
      </b-row>
      <b-card body-class="payment-table p-0">
        <vue-good-table
          :columns="columns"
          :rows="rows"
          :row-style-class="getRowStyle"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
          :sort-options="{ enabled: false }"
        >
          <template
            slot="table-column"
            slot-scope="props"
          >
            <span
              v-if="props.column.field === 'name'"
              class="d-flex"
            >
              {{ props.column.label }}
              <table-header-sort
                :sort-options="sortOptions"
                :sort-label="'NAME'"
                @update:sort-options="sortOptions = $event"
              />
            </span>

            <span
              v-else-if="props.column.field === 'pay_rate'"
              class="d-flex justify-content-end"
            >
              {{ props.column.label }}
              <table-header-sort
                v-if="canEditPayment"
                :sort-options="sortOptions"
                :sort-label="'PAY_RATE'"
                @update:sort-options="sortOptions = $event"
              />
            </span>

            <span
              v-else-if="props.column.field === 'total_pay'"
              class="d-flex justify-content-end"
            >
              {{ props.column.label }}
              <table-header-sort
                v-if="canEditPayment"
                :sort-options="sortOptions"
                :sort-label="'BILLABLE_AMOUNT'"
                @update:sort-options="sortOptions = $event"
              />
            </span>

            <span v-else-if="props.column.field === 'management_fee'">
              {{ $t('Management Fee') }}
              <feather-icon
                v-if="companyManagementInfo?.__typename === 'PercentageBased'"
                v-b-tooltip.hover="$t('management-fee.percentage-based-info', { percentage: companyManagementInfo?.value })"
                icon="InfoIcon"
                class="mb-1"
              />
            </span>
          </template>

          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'name'">
              <div class="d-flex align-items-center">
                <b-avatar
                  :src="getAvatar(props.row.avatar)"
                  :variant="getRandomVariant()"
                  badge
                  :badge-variant="props.row.isOnline ? 'success' : 'secondary'"
                  :text="getInitials(props.row.name)"
                />
                <p class="mb-0 ml-1">{{ IS_MOBILE() ? FORMAT_NAME(props.row.name) : props.row.name }}</p>
              </div>
            </span>
            <span v-else-if="props.column.field === 'total_time'">
              {{ CONVERT_HM(props.row.total_time) }}
              <feather-icon
                v-if="props.row.manual_duration"
                v-b-tooltip.hover="`${$t('Manual Time')}: ${CONVERT_HM(props.row.manual_duration)}`"
                icon="InfoIcon"
                class="mb-1"
              />
            </span>
            <span v-else-if="props.column.field === 'pay_rate'">
              <span
                :class="!props.row.isAmountBlur ? 'blurry-text' : ''"
                @click="props.row.isAmountBlur = !props.row.isAmountBlur"
              >
                {{ props.row.currency }} {{ number_format(props.row.pay_rate ?? 0, 2) }}
                <feather-icon
                  v-if="props.row.pay_rate"
                  v-b-tooltip.hover="`${$t('Effective From')}: ${props.row.effectiveFrom}`"
                  icon="InfoIcon"
                  class="mb-1"
                />
              </span>
            </span>
            <span v-else-if="props.column.field === 'management_fee'">
              <span
                v-if="companyManagementInfo"
                :class="!props.row.isAmountBlur ? 'blurry-text' : ''"
                @click="props.row.isAmountBlur = !props.row.isAmountBlur"
              >
                {{ props.row.currency }} {{ number_format(companyManagementInfo?.__typename === 'MonthlyCharge' ?
                  companyManagementInfo?.value : props.row.total_pay * companyManagementInfo?.value / 100 ?? 0, 2) }}
              </span>
              <span v-else>
                -
              </span>
            </span>
            <span v-else-if="props.column.field === 'manual_duration'">
              {{ CONVERT_HM(props.row.manual_duration) }}
            </span>
            <span
              v-else-if="props.column.field === 'action'"
              class="d-flex align-items-center"
              :class="!canViewPayment || (!props.row.verifiedAt && !props.row.rejectedAt && props.row.invoicedAt) || ((props.row.verifiedAt || props.row.rejectedAt) && !props.row.paidOn) ? 'justify-content-between' : 'justify-content-end'"
            >
              <div v-if="canViewPayment">
                <div v-if="!props.row.verifiedAt && !props.row.rejectedAt && props.row.invoicedAt">
                  <div
                    v-b-tooltip.hover="IS_MOBILE() ? '' : `${$t('Verification Pending by')} ${props.row.name}`"
                    size="sm"
                    class="pending-badge small bg-warning text-center font-weight-bold rounded-lg text-light"
                    variant="warning"
                  >
                    <p class="mb-0">{{ $t('Pending Verification') }}</p>
                  </div>
                </div>

                <div v-else-if="props.row.verifiedAt && !props.row.paidOn">
                  <div
                    v-b-tooltip.hover="IS_MOBILE() ? '' : $t('Payment Pending')"
                    v-b-modal.invoice-history-modal
                    class="pending-badge small bg-warning text-center font-weight-bold rounded-lg text-light"
                    @click="userToShowHistory = props.row"
                  >
                    <p class="mb-0">{{ $t('Payment Pending') }}</p>
                  </div>
                </div>

                <div v-else-if="props.row.rejectedAt && !props.row.updator">
                  <div
                    v-b-tooltip.hover="IS_MOBILE() ? '' : `${$t('Invoice Rejected by')} ${props.row.name}`"
                    size="sm"
                    class="pending-badge small bg-warning text-center font-weight-bold rounded-lg text-light"
                    variant="danger"
                  >
                    <p class="mb-0">{{ $t('Invoice Rejected') }}</p>
                  </div>
                </div>
              </div>

              <div>
                <b-button
                  id="tour-invoice-history"
                  v-b-tooltip.hover="IS_MOBILE() ? '' : $t('View Invoice History')"
                  v-b-modal.invoice-history-modal
                  variant="outline-primary"
                  size="sm"
                  @click="showInvoiceHistory(props.row)"
                >
                  <i class="fa fa-history" />
                </b-button>

                <b-dropdown
                  v-if="canEditPayment"
                  id="payment-options"
                  variant="link"
                  no-caret
                >
                  <template #button-content>
                    <feather-icon icon="MoreVerticalIcon" />
                  </template>
                  <b-dropdown-item
                    id="tour-generate-invoice"
                    variant="outline-success"
                    size="sm"
                    @click="showInvoice(props.row)"
                  >
                    <i class="fa fa-file-invoice" />
                    {{ $t('payments.generate-invoice') }}
                  </b-dropdown-item>

                  <b-dropdown-item
                    id="tour-payroll-settings"
                    size="sm"
                    variant="outline-primary"
                    @click="showPaymentSettingsModal(props.row)"
                  >
                    <i class="fa fa-cog" />
                    {{ $t('payments.payroll-settings') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </span>
            <span v-else-if="props.column.field === 'total_pay'">
              <span
                v-if="props.row.total_pay !== null"
                :class="!props.row.isAmountBlur ? 'blurry-text' : ''"
                @click="props.row.isAmountBlur = !props.row.isAmountBlur"
              >
                {{ props.row.currency }} {{ number_format(props.row.total_pay, 2) }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'payment_method'">
              <b-form-select
                :value="props.row.payment_method"
                :options="paymentMethodOptions"
              />
            </span>
            <span v-else-if="props.column.field === 'paidUpTo'">
              <p class="mb-0">
                {{ timeWithTimeZone(props.row.paidUpTo, $store.state.project.company.timezoneOffset.name,
                                    COMMON.DATE_FORMAT) }}
              </p>
            </span>
          </template>
        </vue-good-table>
      </b-card>

      <div class="w-100 centralize">
        <b-pagination
          v-if="totalItems > perPage"
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="perPage"
          @input="getPayments"
        />
      </div>

      <generate-invoice
        :visible="showGenerateInvoice"
        :user-info="selectedUserInfo"
        :date-range="form"
        @onSave="handleSave"
        @close="showGenerateInvoice = false; selectedUserInfo = {}"
      />
      <invoice-history
        :user-info="userToShowHistory"
        :date-range="form"
        @close="userToShowHistory = null"
        @refetch="getPayments"
      />
      <!-- Payroll Settings -->
      <update-payment-setting
        v-if="selectedUserInfo"
        :user-info="selectedUserInfo"
        @onSave="getPayments"
        @close="() => { selectedUserInfo = {} }"
      />
    </b-overlay>
  </div>
</template>
<script>
import {
  BAvatar, BFormSelect, BCard, BButton, VBTooltip, BDropdown, BDropdownItem, BOverlay, BPagination, BFormCheckbox,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { mapActions, mapGetters } from 'vuex'
import EventBus from '@/event-bus'
import { checkPermission } from '@/utils/permissions'
import ManagePermissions from '@/views/common/components/ManagePermissions.vue'
import useApollo from '@/plugins/graphql/useApollo'
import { setComponentReady } from '@/views/tour/tour'
import moment from 'moment'
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue'
import InvoiceHistory from './InvoiceHistory.vue'
import UpdatePaymentSetting from './UpdatePaymentSetting.vue'
import GenerateInvoice from './GenerateInvoice.vue'

export default {
  components: {
    VueGoodTable,
    BAvatar,
    BFormSelect,
    BCard,
    BButton,
    BDropdown,
    BDropdownItem,
    BOverlay,
    BFormCheckbox,
    BPagination,
    GenerateInvoice,
    InvoiceHistory,
    ManagePermissions,
    UpdatePaymentSetting,
    TableHeaderSort,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    form: {
      type: Object,
      default: () => { },
    },
    dateRange: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchTerm: '',
      isAmountBlur: false,
      perPage: 10,
      totalItems: 0,
      currentPage: 1,
      showInvite: false,
      paymentMethodOptions: [
        {
          value: 'manual', text: this.$t('Manual'),
        },
        {
          value: 'automatic', text: this.$t('Automatic'),
        },
      ],
      showGenerateInvoice: false,
      selectedUserInfo: {},
      userToShowHistory: null,
      permissions: ['payment.edit', 'payment.view', 'project.viewusers'],
      needPermissionCheck: false,
      companyManagementInfo: null,
      rows: [],
      sortOptions: { sortBy: 'NAME', sortDirection: 'ASC' },
      isProcessing: false,
      totalTime: 0,
      totalAmount: 0,
      includeInactive: false,
    }
  },
  computed: {
    ...mapGetters('project', ['USER_ROLE']),
    ...mapGetters('permissions', ['canManagePermissions', 'canViewPayment', 'canEditPayment', 'canViewUsers']),
    columns() {
      return [
        {
          label: this.$t('table.name'), field: 'name', thClass: 'sticky-col', tdClass: 'sticky-col',
        },
        { label: this.$t('payments.total-time'), field: 'total_time' },
        {
          label: this.$t('Current Pay Rate'), field: 'pay_rate', tdClass: 'col-160', type: 'number',
        },
        {
          label: this.$t('Billable Amount'), field: 'total_pay', tdClass: 'billable-amount', type: 'number',
        },
        ...(this.canEditPayment && this.companyManagementInfo ? [{ label: this.$t('Management Fee'), field: 'management_fee' }] : []),
        { label: this.$t('Last Payment'), field: 'paidUpTo' },
        {
          label: this.$t('Action'), field: 'action', width: '250px',
        },
      ]
    },
    timezoneOffset() {
      return this.$store.state.project.company.timezoneOffset.filter
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.getPayments()
      },
    },
    sortOptions: {
      handler() {
        this.getPayments()
      },
      deep: true,
    },
  },
  mounted() {
    this.getPayments()
    this.getCompanyManagementFee()
    EventBus.$on('permission-updated', permission => {
      if (this.permissions.indexOf(permission) !== -1) {
        this.needPermissionCheck = true
        setTimeout(() => this.checkUserPermissions(), 2000)
      }
    })
    this.checkUserPermissions()
  },
  beforeDestroy() {
    EventBus.$off('permission-updated')
  },
  methods: {
    ...mapActions('permissions', ['setPermissions']),
    checkUserPermissions() {
      if (!(this.canViewPayment && this.canEditPayment && this.canViewUsers) || this.needPermissionCheck) {
        checkPermission(this.permissions, permissions => {
          const canEditPayment = permissions['payment.edit'] === true
          const canViewPayment = permissions['payment.view'] === true
          const canViewUsers = permissions['project.viewusers'] === true

          const payload = [
            { key: 'setPaymentViewPermission', value: canViewPayment },
            { key: 'setPaymentEditPermission', value: canEditPayment },
            { key: 'setUserViewPermission', value: canViewUsers },
          ]

          this.setPermissions(payload)
          this.needPermissionCheck = false
        })
      }
    },
    getRowStyle(row) {
      return row.projectRoles.data[0]?.deactivatedAt ? 'bg-light-danger' : ''
    },

    showInvoice(user) {
      this.showGenerateInvoice = true
      this.selectedUserInfo = user
    },
    handleSave() {
      this.showGenerateInvoice = false
      this.selectedUserInfo = {}
      this.getPayments()
    },
    showPaymentSettingsModal(user) {
      this.selectedUserInfo = user
      this.$nextTick(async () => {
        this.$bvModal.show('update-payment-setting-modal')
      })
    },
    async showInvoiceHistory(row) {
      this.userToShowHistory = row
    },
    getCompanyManagementFee() {
      useApollo.company.getCompanyManagementFee({
        companyUid: this.$store.state.project.selectedCompany,
      }).then(response => {
        this.companyManagementInfo = response.data.company.managementInfo?.settings?.[0]?.information
      })
    },
    getPayments() {
      this.isProcessing = true
      const currency = this.$store.state.project.company?.currency ?? '$'
      const projectUUid = this.$store.state.project.selectedProject
      const { sortBy, sortDirection } = this.sortOptions

      useApollo.payroll
        .getUserPayments({
          first: this.perPage,
          page: this.currentPage,
          companyId: this.$store.state.project.selectedCompany,
          startTime: moment(this.form.start_date).format('YYYY-MM-DD HH:mm:ss'),
          endTime: moment(this.form.end_date).add(1, 'days').format('YYYY-MM-DD HH:mm:ss'),
          timezone: this.timezoneOffset,
          projectUUid,
          selectedUser: this.form.userId?.length > 0 ? this.form.userId : null,
          sortBy,
          sortDirection,
          includeInactive: this.includeInactive,
        })
        .then(response => {
          const { activityAmount, activityDuration } = response.data.project
          this.totalAmount = activityAmount
          this.totalTime = activityDuration
          this.totalItems = response.data.users.total
          this.rows = response.data.users.data.map(payment => ({
            name: payment.name,
            avatar: payment.avatar,
            total_time: payment.activityDuration,
            manual_time: 0,
            currency,
            adjusted_amount: 0,
            pay_rate: `${payment.paymentSettings?.data[0]?.amount}`,
            effectiveFrom: this.timeWithTimeZone(
              payment.paymentSettings?.data[0]?.effectiveFrom,
              this.timezoneOffset,
              this.COMMON.DATE_FORMAT,
            ),
            projectRoles: payment.projectRoles,
            invoicedAt: payment.invoices.data.length ? this.timeWithTimeZone(payment.invoices?.data[0]?.invoicedAt, this.timezoneOffset, this.COMMON.DATE_FORMAT) : null,
            verifiedAt: payment.invoices?.data[0]?.verifiedAt,
            rejectedAt: payment.invoices?.data[0]?.rejectedAt,
            paidOn: payment.invoices?.data[0]?.paidOn,
            total_pay: payment.activityAmount,
            action: 'manual',
            manual_duration: payment.manualDuration,
            uuid: payment.uuid,
            isAmountBlur: false,
            paidUpTo: payment.invoices.data[0]?.to,
            updator: payment.invoices.data[0]?.updator,
          }))
          setComponentReady()
        })
        .catch(error => {
          this.showErrorMessage(error)
        })
        .finally(() => {
          this.isProcessing = false
        })
    },
  },
}
</script>
<style>
.billable-amount {
  font-weight: bold;
}

.payment-table .pending-badge {
  width: 96px;
  line-height: 1.4;
  padding: 4px;
}
</style>
